import { Link } from "react-router-dom";
import { FormActions } from "../../components/common/FormActions";
import { Table } from "../../components/common/Table";
import { TTableColumn } from "../../types/common";
import {
  beautify,
  createDeleteAction,
  createSelectItem,
  createViewAction,
  createViewEdit,
  downloadThroughAnchorLink,
  formatDate,
  getResultOptions,
  itemExists,
  numberFormat,
  returnObject,
  useGetUserPermissions,
} from "../../utils/helpers";
import {
  APPROVED,
  CAN_ADD_PRODUCT_TO_PURCHASE_ORDERS,
  CAN_APPROVE_PURCHASE_ORDER,
  CAN_EDIT_PURCHASE_ORDER,
  DELIVERED,
  FORM_DATA,
  MODAL,
  REJECTED,
  SUBMITTED,
} from "../../utils/constants";
import { Add } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { sourceApis } from "../handlers/useApiHandler";
import {
  API_BULK_PURCHASE_ORDERS_TEMPLATE,
  API_PURCHASE_ORDERS,
} from "../../utils/apis";
import toast from "react-hot-toast";
import { productItemFields } from "../inventory/useProductForm";
import { GenericActionForm } from "../../components/common/action_forms/GenericActionForm";
import GenericTabView from "../../components/layouts/GenericTabView";
import styled from "styled-components";
import { ExportButton } from "../../components/common/Buttons";
import { BASE_API_URL } from "../../config";

const finalStates = [REJECTED, DELIVERED];

export const usePurchaseOrder = () => {
  const tableColumns = (): TTableColumn[] => [
    {
      id: "purchase_order_number",
      header: "Order Number",
      filter: {
        type: "text",
      },
    },
    {
      id: "supplier_name",
      header: "Supplier",
      filter: {
        type: "text",
        key: "supplier_name",
      },
    },
    {
      id: "purchase_order_date",
      header: "Purchase Order Date",
      filter: {
        type: "date",
      },
    },
    {
      id: "payment_type",
      header: "Payment Type",
      filter: {
        type: "text",
      },
    },
    {
      id: "total_amount",
      header: "Total Amount",
    },
    { id: "actions", header: "Actions" },
  ];

  const permissions = useGetUserPermissions();
  const canEditPurchaseOrder = itemExists(permissions, [
    CAN_EDIT_PURCHASE_ORDER,
  ]);

  const tableData = (data: any) =>
    data.map((item: any) => ({
      ...item,
      purchase_order_number: (
        <Link
          className="link-with-icon"
          to={`/purchase_order/view/${item?.id}`}
        >
          {item?.purchase_order_number}
        </Link>
      ),
      total_amount: numberFormat(item?.total_amount),
      actions: (
        <FormActions
          actions={[
            ...createViewEdit(
              "/purchase_order",
              item?.id,
              canEditPurchaseOrder,
            ),
            ...returnObject(canEditPurchaseOrder, [
              createDeleteAction(
                `${API_PURCHASE_ORDERS}${item?.id}/`,
                `Purchase Order ${item?.purchase_order_number}`,
              ),
            ]),
          ]}
        />
      ),
    }));

  const orderItemsTableColumns = (): TTableColumn[] => [
    { id: "item", header: "Item" },
    { id: "quantity", header: "Quantity Requested" },
    { id: "quantity_received", header: "Quantity Received" },
    { id: "unit_price", header: "Unit Price" },
    { id: "total_price", header: "Total Amount" },
    { id: "condition", header: "Condition" },
    { id: "actions", header: "Actions" },
  ];

  const orderItemstableData = (data: any) =>
    data.map((item: any) => ({
      ...item,
      condition: beautify(item?.condition),
      unit_price: numberFormat(item?.unit_price),
      total_price: numberFormat(item?.total_price),
      quantity_recieved: item?.quantity_recieved || "0",
      item: (
        <Link to={`/inventory/view/${item?.inventory?.id}`}>
          {item?.inventory?.name}
        </Link>
      ),
      actions: (
        <FormActions
          actions={[createViewAction("/purchase_order_items", item?.id)]}
        />
      ),
    }));

  const actionTabs = (data: any) => [
    {
      title: data?.status === APPROVED ? "Add Products" : "Action Form",
      component: (
        <GenericActionForm data={data} source_id="purchase_order_actions" />
      ),
    },

    ...returnObject(data?.status === APPROVED, [
      {
        title: "Bulk Upload",
        component: (
          <GenericActionForm
            data={data}
            source_id="bulk_purchase_order"
            // setReload={setReload}
          />
        ),
      },
    ]),
  ];

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "Order Information",
          fields: {
            purchase_order_number: item?.purchase_order_number,
            supplier: item?.supplier_name,
            purchase_order_date: item?.purchase_order_date,
            payment_type: item?.payment_type,
            total_amount: numberFormat(item?.total_amount),
            created_at: formatDate(item?.created_at),
            status: item?.status,
          },
        },
        {
          title: "Items Details",
          fields: {},
          component: (
            <Table
              loading={false}
              count={item?.order_items?.count}
              tableTitle="Items Details"
              tableColumns={
                orderItemsTableColumns ? orderItemsTableColumns() : []
              }
              tableData={
                item?.order_items ? orderItemstableData(item?.order_items) : []
              }
              // filters={filters}
              // onFilterChange={setFilter}
              // status={status}
            />
          ),
        },
        {
          title: "Action Form",
          fields: {},
          type: "component",
          component: <GenericTabView tabs={actionTabs(item)} />,
          visible: !finalStates.includes(item?.status),
        },
      ],
    };
  };
  const activityFilter = "purchase_order";

  return { tableColumns, tableData, viewData, activityFilter };
};

const getConfigOptions = (data: any) => {
  const [inventory, setInventory] = useState(data?.order_items);
  useEffect(() => {
    setInventory(data?.order_items);
  }, [data]);
  return {
    inventories: getResultOptions(inventory, "inventory_name", "id"),
  };
};

export function usePurchaseOrderActions({ form, data }) {
  // action select options
  let actionsList = [APPROVED, REJECTED, DELIVERED];

  if (data?.status === "submitted") {
    actionsList = [APPROVED, REJECTED];
  }
  if (data?.status === APPROVED) {
    actionsList = [DELIVERED];
  }
  const permissions = useGetUserPermissions();
  const canAddProductToPurchaseOrder = itemExists(permissions, [
    CAN_ADD_PRODUCT_TO_PURCHASE_ORDERS,
  ]);

  const canApprovePurchaseOrder = itemExists(permissions, [
    CAN_APPROVE_PURCHASE_ORDER,
  ]);

  const actions = [...actionsList.map((action) => createSelectItem(action))];

  // const remainingItems = data?.order_items?.filter(
  //   (item) => item.quantity_received < item.quantity,
  // );

  // construct action form fields
  const recommendationFields = [
    {
      field_name: "comment",
      label: "comment",
      type: "textarea",
      props: { wide: true },
    },
  ];

  const validateQuantity = () => {
    const selectedInventory = data?.order_items?.find(
      (item) => item.id === form.purchase_order_inventory,
    );
    if (!selectedInventory) return false;
    let totalQuantityReceived = form?.products?.length || 0;

    totalQuantityReceived += selectedInventory.quantity_received;

    if (totalQuantityReceived === selectedInventory.quantity) {
      return false;
    } else if (totalQuantityReceived > selectedInventory.quantity) {
      toast.dismiss();
      toast.error(
        `Quantity received (${totalQuantityReceived}) exceeds ordered quantity (${selectedInventory.quantity})`,
      );
      return false;
    }
    return true;
  };

  const productFields = [
    {
      field_name: "purchase_order_inventory",
      label: "Select Inventory Item",
      type: "select",
      props: { required: true },
      options_name: "inventories",
    },
    {
      field_name: "products",
      label: "Add Recieved Items",
      type: MODAL,
      visible:
        form?.purchase_order_inventory !== undefined &&
        form?.purchase_order_inventory !== "",
      props: {
        required: true,
        disabled: !validateQuantity(),
        icon: <Add fontSize="small" />,
        wide: true,
      },
      fields: productItemFields,
    },
  ];

  const fields = [
    ...returnObject(data?.status === SUBMITTED && canApprovePurchaseOrder, [
      {
        field_name: "status",
        type: "select",
        props: { required: true },
        options_data: actions,
      },
      ...recommendationFields,
    ]),
    ...returnObject(
      data?.status === APPROVED && canAddProductToPurchaseOrder,
      productFields,
    ),
  ];

  const options = getConfigOptions(data);

  function onSubmit(payload: any, id: any) {
    if (id) {
      let api = sourceApis["purchase_order"];
      if (data.status === APPROVED) {
        api = `${API_PURCHASE_ORDERS}${id}/products/`;
        id = null;
      }
      if (!api) return;
      return {
        id,
        api,
        params: { ...payload },
      };
    }
  }

  const enableSubmit = Object.keys(form).length > 0;

  return { fields, enableSubmit, options, finalStates, onSubmit };
}

export function usePurchaseOrderBulkUpload({ form, data }) {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadTemplate = () => {
    setIsDownloading(true);
    downloadThroughAnchorLink(
      `${BASE_API_URL}${API_BULK_PURCHASE_ORDERS_TEMPLATE}`,
      "bulk_purchase_order_template.xlsx",
    ).finally(() => {
      setIsDownloading(false);
    });
  };
  const fields = [
    {
      field_name: "bulk_template",
      type: "component",
      component: (
        <TemplateWrapper>
          <span>Download Bulk Template</span>
          <div>
            <ExportButton
              text="template"
              title="Download Bulk Template"
              onClick={() => handleDownloadTemplate()}
              isDownloading={isDownloading}
            />
          </div>
        </TemplateWrapper>
      ),
    },
    {
      field_name: "purchase_order_inventory",
      type: "select",
      label: "Select Inventory Item",
      props: { required: true, newLine: true },
      options_name: "inventories",
    },
    {
      field_name: "file",
      type: "file",
      label: "Bulk File",
      props: {
        required: true,
        accept: ".xlsx,.xls",
      },
    },
  ];

  function onSubmit(payload: any, id: any) {
    const orderItemId = payload?.purchase_order_inventory;
    if (!orderItemId) {
      toast.error("Please select an inventory item");
      return;
    }
    let api = "";
    if (id) {
      if (data.status === APPROVED) {
        api = `order-inventories/${orderItemId}/batch-products-upload/`;
        id = null;
      }
      if (!api) return;
      return {
        id,
        api,
        params: {
          file: payload.file.file[0],
        },
        type: FORM_DATA,
      };
    }
  }
  const enableSubmit = Object.keys(form).length > 0;
  const options = getConfigOptions(data);

  return { fields, onSubmit, finalStates, enableSubmit, options };
}

const TemplateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 12px;
`;
